import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Star, Gem } from 'lucide-react';
import gsap from 'gsap';
import { getBannerData, performPull } from '../logic/bannerLogic';
import HUD from './HUD';
import BottomNav from './BottomNav';

function Banners({ app, bannerId }) {
  const bannersRef = useRef(null);
  const { banners, premiumCurrency } = getBannerData();
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [direction, setDirection] = useState('left');

  useEffect(() => {
    if (bannerId) {
      const index = banners.findIndex(banner => banner.id === bannerId);
      if (index !== -1) {
        setCurrentBannerIndex(index);
      }
    }
  }, [bannerId, banners]);

  const handlePull = (banner, count) => {
    const cost = count * banner.cost;
    if (premiumCurrency < cost) {
      return;
    }

    const pulls = performPull(banner, count);
    if (pulls && pulls.length > 0) {
      app.goToPull(pulls);
    }
  };

  const goToPreviousBanner = () => {
    if (currentBannerIndex > 0) {
      setDirection('right');
      setCurrentBannerIndex(currentBannerIndex - 1);
    }
  };

  const goToNextBanner = () => {
    if (currentBannerIndex < banners.length - 1) {
      setDirection('left');
      setCurrentBannerIndex(currentBannerIndex + 1);
    } else {
      setDirection('left');
      setCurrentBannerIndex(0);
    }
  };

  const selectBanner = (index) => {
    setDirection(index > currentBannerIndex ? 'left' : 'right');
    setCurrentBannerIndex(index);
  };

  useEffect(() => {
    const startX = direction === 'left' ? 100 : -100;

    gsap.from(bannersRef.current.children, {
      x: startX,
      opacity: 0,
      duration: 0.5,
      stagger: 0.2,
      ease: "back.out(1.4)"
    });
  }, [currentBannerIndex, direction]);

  return (
    <div className="bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] w-full h-full mx-auto relative overflow-hidden text-white">
      <style>
        {`
          @keyframes shine {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          .featured-banner::after {
            content: '';
            position: absolute;
            top: -100%;
            left: -100%;
            width: 300%;
            height: 300%;
            background: linear-gradient(
              45deg,
              transparent 0%,
              transparent 45%,
              rgba(255, 255, 255, 0.2) 48%,
              rgba(255, 255, 255, 0.4) 50%,
              rgba(255, 255, 255, 0.2) 52%,
              transparent 55%,
              transparent 100%
            );
            animation: shine 3s infinite;
            z-index: 15;
            pointer-events: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .navigation-button {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(0, 0, 0, 0.5);
            border: none;
            color: white;
            padding: 10px;
            border-radius: 50%;
            cursor: pointer;
            z-index: 20;
          }
          .navigation-button.left {
            left: 20px;
          }
          .navigation-button.right {
            right: 20px;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-[url('/assets/backgrounds/background.png')] opacity-5 bg-cover bg-center" />
      
      <div className="p-6 flex flex-col h-full relative z-10">
        <div className="flex-1 relative mb-16">
          <div ref={bannersRef} className="w-full h-[calc(100%-4rem)]">
            {banners.map((banner, index) => (
              <div
                key={index}
                className={`relative w-full h-full rounded-xl overflow-hidden group ${banner.type === 'featured' ? 'featured-banner' : ''}`}
                style={{ display: index === currentBannerIndex ? 'block' : 'none' }}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${banner.bannerTitle.bgColor} z-10`} />
                <img
                  src={banner.image}
                  alt={banner.bannerTitle.lines.join(' ')}
                  className="absolute h-full w-full object-cover transition-opacity"
                  style={{ objectPosition: banner.highlight?.objectPosition || 'center' }}
                />
                <div className="absolute top-6 left-8 z-20 w-1/2">
                  <h2 className="text-4xl font-bold tracking-wider leading-tight" style={{ fontFamily: 'Norse', textShadow: '0 0 10px rgba(255,255,255,0.5)' }}>
                    {banner.bannerTitle.lines.map((line, i) => (
                      <span key={i} className={`block ${line.color}`}>
                        {line.text}
                      </span>
                    ))}
                  </h2>
                  {banner.description && (
                    <p className="mt-4 text-lg text-white/90 bg-black/30 p-3 rounded-lg backdrop-blur-sm">
                      {banner.description}
                    </p>
                  )}
                </div>
                {banner.type === 'new' && (
                  <div className="absolute top-4 right-4 z-20 bg-red-500 px-3 py-1 rounded-full text-white font-bold">
                    NEW
                  </div>
                )}
                {banner.type === 'featured' && (
                  <div className="absolute top-4 right-4 z-20 bg-yellow-400/90 px-3 py-1 rounded-full text-black font-bold">
                    FEATURED
                  </div>
                )}
                <div className="absolute bottom-10 left-6 right-6 z-20">
                  <div className="inline-block bg-yellow-500/90 text-black font-semibold px-4 py-2 rounded-lg mb-4">
                    Time Remaining: {banner.timeRemaining}
                  </div>
                  <div className="flex justify-between">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePull(banner, 1);
                      }}
                      className="bg-gradient-to-b from-[#e6d5a7] to-[#c6b178] hover:brightness-110 px-6 py-3 rounded-lg flex items-center gap-2 w-full mr-2 text-gray-900 font-semibold shadow-lg"
                    >
                      <span>Pull 1</span>
                      <div className="flex items-center gap-1">
                        <Star size={16} className="text-yellow-700" />
                        <span>{banner.cost}</span>
                      </div>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePull(banner, 10);
                      }}
                      className="bg-gradient-to-b from-[#e8f4ff] to-[#b5d5f5] hover:brightness-110 px-6 py-3 rounded-lg flex items-center gap-2 w-full ml-2 text-gray-900 font-semibold shadow-lg"
                    >
                      <span>Pull 10</span>
                      <div className="flex items-center gap-1">
                        <Star size={16} className="text-blue-700" />
                        <span>{banner.cost * 10}</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="absolute top-20 right-4 z-30 flex flex-col gap-2">
            {banners.map((banner, index) => (
              <div
                key={index}
                onClick={() => selectBanner(index)}
                className={`w-12 h-12 rounded-full overflow-hidden cursor-pointer border-2 transition-all ${
                  index === currentBannerIndex ? 'border-yellow-400 scale-110' : 'border-gray-600 hover:border-gray-400'
                }`}
              >
                <img
                  src={banner.image}
                  alt={banner.bannerTitle.lines.join(' ')}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: banner.highlight?.objectPosition || 'center' }}
                />
              </div>
            ))}
          </div>
        </div>

        <button 
          className="navigation-button left"
          onClick={goToPreviousBanner}
          disabled={currentBannerIndex === 0}
        >
          <ArrowLeft size={24} />
        </button>
        <button 
          className="navigation-button right"
          onClick={goToNextBanner}
        >
          <ArrowRight size={24} />
        </button>
      </div>
    </div>
  );
}

export default Banners;
